import { Engine } from "@babylonjs/core/Engines/engine";
import { Scene } from "@babylonjs/core/scene";
import { TargetCamera } from "@babylonjs/core/Cameras/targetCamera";
import { HemisphericLight } from "@babylonjs/core/Lights/hemisphericLight";
import { DirectionalLight } from "@babylonjs/core/Lights/directionalLight";
import { Color3, Color4 } from "@babylonjs/core/Maths/math.color";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { RESOLUTION_LINES, type ResolutionProfile } from "../config.ts";
import { ARENA_COLORS, hex } from "../palette.ts";

/**
 * Owns the Babylon engine and scene, and the "pixelated" look: the scene is
 * rendered at a low vertical resolution and the canvas is upscaled with hard
 * edges (CSS `image-rendering: pixelated`). Fewer pixels also means much less
 * GPU work on phones, so the profiles double as graphics-quality presets.
 */
export class Renderer {
  readonly engine: Engine;
  readonly scene: Scene;
  readonly camera: TargetCamera;
  private readonly canvas: HTMLCanvasElement;
  private profile: ResolutionProfile;

  constructor(canvas: HTMLCanvasElement, profile: ResolutionProfile) {
    this.canvas = canvas;
    this.profile = profile;
    this.engine = new Engine(
      canvas,
      false,
      { stencil: false, preserveDrawingBuffer: false, powerPreference: "high-performance", doNotHandleContextLost: false },
      false,
    );
    this.scene = new Scene(this.engine);
    this.scene.clearColor = Color4.FromColor3(hex(ARENA_COLORS.sky), 1);
    this.scene.ambientColor = new Color3(0.25, 0.25, 0.3);
    this.scene.skipPointerMovePicking = true;
    this.scene.fogMode = Scene.FOGMODE_LINEAR;
    this.scene.fogColor = hex(ARENA_COLORS.sky);
    this.scene.fogStart = 30;
    this.scene.fogEnd = 70;

    const sky = new HemisphericLight("sky", new Vector3(0.2, 1, -0.3), this.scene);
    sky.intensity = 0.85;
    sky.diffuse = new Color3(1, 0.97, 0.92);
    sky.groundColor = new Color3(0.3, 0.32, 0.45);
    const sun = new DirectionalLight("sun", new Vector3(-0.45, -1, 0.35), this.scene);
    sun.intensity = 0.55;

    this.camera = new TargetCamera("camera", new Vector3(0, 10, -12), this.scene);
    this.camera.fov = 0.9;
    this.camera.minZ = 0.3;
    this.camera.maxZ = 120;

    window.addEventListener("resize", () => this.resize());
    window.visualViewport?.addEventListener("resize", () => this.resize());
    this.resize();
  }

  get resolution(): ResolutionProfile {
    return this.profile;
  }

  setResolution(profile: ResolutionProfile): void {
    this.profile = profile;
    this.resize();
  }

  /** Render-target size in game pixels. */
  get renderSize(): { width: number; height: number } {
    return { width: this.engine.getRenderWidth(), height: this.engine.getRenderHeight() };
  }

  render(): void {
    this.engine.beginFrame();
    this.scene.render();
    this.engine.endFrame();
  }

  private resize(): void {
    const dpr = window.devicePixelRatio || 1;
    const lines = RESOLUTION_LINES[this.profile];
    let level: number;
    if (lines === null) {
      // Native, capped at 2× to keep very dense phone screens affordable.
      level = 1 / Math.min(dpr, 2);
    } else {
      // Whole physical pixels per game pixel, so every game pixel has the same size.
      const physicalHeight = (this.canvas.clientHeight || window.innerHeight) * dpr;
      const pixelSize = Math.max(1, Math.round(physicalHeight / lines));
      level = pixelSize / dpr;
    }
    this.engine.setHardwareScalingLevel(level);
    this.engine.resize();
  }
}
